import { useEffect, useState } from 'react';
import { getAllDrops, deleteDrop } from '../services/dropService';
import { formatTimestamp } from '../utils/time';
import { coordsToFrequency } from '../utils/frequency';
import type { LocationDrop } from '../types';

export default function DropsPage() {
  const [drops, setDrops] = useState<LocationDrop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDrops(); }, []);

  async function loadDrops() {
    setLoading(true);
    try { setDrops(await getAllDrops()); }
    finally { setLoading(false); }
  }

  async function handleDelete(id: string) {
    if (!confirm('이 드롭을 삭제하시겠습니까?')) return;
    await deleteDrop(id);
    loadDrops();
  }

  function isExpired(drop: LocationDrop): boolean {
    if (!drop.expiresAt?.toMillis) return true;
    return drop.expiresAt.toMillis() < Date.now();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">드롭 관리</h1>
        <button onClick={loadDrops} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">새로고침</button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">제목</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">작성자</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">위치</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">셀</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">댓글</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">상태</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">만료</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">생성</th>
              <th className="px-3 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">로딩 중...</td></tr>
            ) : drops.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">드롭 없음</td></tr>
            ) : drops.map(drop => (
              <tr key={drop.id} className={`hover:bg-gray-50 ${isExpired(drop) ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 font-medium">{drop.title}</td>
                <td className="px-3 py-2">{drop.authorNickname}</td>
                <td className="px-3 py-2 text-xs font-mono">{coordsToFrequency(drop.lat, drop.lng)}</td>
                <td className="px-3 py-2">{drop.cellsSpent?.toLocaleString()}</td>
                <td className="px-3 py-2">{drop.commentCount}</td>
                <td className="px-3 py-2">
                  {isExpired(drop)
                    ? <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">만료</span>
                    : <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">활성</span>
                  }
                </td>
                <td className="px-3 py-2 text-gray-500 text-xs">{formatTimestamp(drop.expiresAt, 'short')}</td>
                <td className="px-3 py-2 text-gray-500 text-xs">{formatTimestamp(drop.createdAt, 'short')}</td>
                <td className="px-3 py-2">
                  <button onClick={() => handleDelete(drop.id)} className="text-xs text-red-500 hover:underline">삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">{drops.length}개 드롭</div>
      </div>
    </div>
  );
}
